import { useState } from "react";
import { Scrollama, Step } from "react-scrollama";
import MirroringViz from "./MirroringViz";
import TextBox from "../TextBox";
import {
  SectionContainer,
  ScrollingContainer,
  StepContainer,
  SectionSubheader,
} from "../SharedUI";
import styled from "@emotion/styled";

const MirroringScrollingContainer = styled(ScrollingContainer)`
  padding-bottom: 30vh;

  @media (max-width: 900px) {
    padding-bottom: 50vh;
  }
`;

export default function Mirroring() {
  const [currentStep, setCurrentStep] = useState("");

  const onStepEnter = ({ data }: { data: string }) => {
    setCurrentStep(data);
  };

  return (
    <SectionContainer>
      <MirroringViz currentStep={currentStep} />
      <MirroringScrollingContainer>
        <Scrollama onStepEnter={onStepEnter}>
          <Step data={"mirroring-intro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "mirroring-intro"}>
                <SectionSubheader>
                  1.2 How Does the Growth Lab Address the Reporting Problem?
                </SectionSubheader>
                <p>
                  Trade is one of the few administrative datasets recorded
                  twice: once by the exporter and once by the importer.
                  Combining these two reports—often called mirroring—sounds
                  straightforward, but in practice the numbers often don't
                  match.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"average"}>
            <StepContainer>
              <TextBox isActive={currentStep === "average"}>
                <p>
                  One solution might be to take a simple average but that would
                  treat both sides as equally reliable, which is not the case.
                  Instead, we need an approach that gives greater weight to the
                  more trusted source.
                </p>
                <p>
                  Another approach might be to rely on external proxies of
                  "quality", such as income level or governance scores. However,
                  these measures tend to be incomplete over time and weak
                  predictors of reporting accuracy.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"methodology-intro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "methodology-intro"}>
                <p>
                  Instead, the Growth Lab evaluates discrepancies across the
                  entire network of trade relationships to infer how
                  consistently each country reports trade, separately from
                  reported trade volumes. A key constraint however is that
                  countries cannot be penalized for discrepancies driven by
                  unreliable partners. A large mismatch with an unreliable
                  partner is less informative than a smaller mismatch with a
                  consistently reliable one. For this reason, reporting
                  reliability is inferred in a network-aware manner, using
                  network metrics described in our published paper.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"reliability"}>
            <StepContainer>
              <TextBox isActive={currentStep === "reliability"}>
                <p>
                  From here, we combine the two reports into a single best
                  estimate using a reliability-weighted blend. More reliable
                  reporters receive greater weight. The result is transparent,
                  reproducible, and adaptive over time, providing a practical
                  way to turn two messy numbers into one trusted measure of
                  actual trade flow.
                </p>
                <p>
                  Returning to the{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  and{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>{" "}
                  example, suppose{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  is generally more consistent in its reporting, with fewer
                  discrepancies across reliable partners.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"weighting"}>
            <StepContainer>
              <TextBox isActive={currentStep === "weighting"}>
                <p>
                  In that case,{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>
                  's reported value receives greater weight when estimating the
                  true trade flow between the two.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"mirroring-outro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "mirroring-outro"}>
                <p>
                  Applied to all country pairs, this method reconciles
                  conflicting reports and produces a more accurate and unified
                  view of global trade.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
        </Scrollama>
      </MirroringScrollingContainer>
    </SectionContainer>
  );
}
